import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Offcanvas, OffcanvasHeader, OffcanvasBody } from 'reactstrap';
import Pagination from './Pagination';

const IssuesDisplay = () => {
  const [issues, setIssues] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const toggleCanvas = () => setIsOpen(!isOpen);

  const fetchIssues = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/issues`, {
        params: {
          page,
          limit: 50,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.data.data && response.data.pagination) {
        setIssues(response.data.data);
        setPagination(response.data.pagination);
      } else {
        // Fallback for old API format
        setIssues(Array.isArray(response.data) ? response.data : []);
        setPagination(null);
      }
    } catch (err) {
      console.error('Failed to fetch issues:', err);
      setError('Failed to fetch issues');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (issue) => {
    setSelectedIssue(issue);
    setIsOpen(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchIssues(page);
  };

  const handleStatusUpdate = async (status) => {
    if (!selectedIssue) return;

    setUpdatingStatus(true);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/admin/issues/${selectedIssue.id}/status`,
        { status },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      // Update the selected issue with new status
      setSelectedIssue(prev => ({
        ...prev,
        status: response.data.issue.status,
      }));

      // Refresh the issues list
      await fetchIssues(currentPage);
    } catch (err) {
      console.error('Failed to update issue status:', err);
      alert('Failed to update issue status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  useEffect(() => {
    fetchIssues(1);
  }, []);

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div>
      <table style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
        <thead style={{
          backgroundColor: 'rgb(250, 248, 255)',
          borderTop: '1px solid rgb(233, 230, 241)',
          borderBottom: '1px solid rgb(233, 230, 241)',
        }}>
          <tr className='link-row border-bottom'>
            <th className="p-3 text-center">User</th>
            <th className="p-3 text-center">Title</th>
            <th className="p-3 text-center">Status</th>
            <th className="p-3 text-center">Created</th>
            <th className="p-3 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {issues.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center">No issues found</td>
            </tr>
          ) : (
            issues.map(issue => {
              const getStatusBadgeColor = (status) => {
                switch (status) {
                  case 'Resolved': return '#28a745';
                  case 'Valid Bug': return '#007bff';
                  case 'Invalid Bug': return '#dc3545';
                  case 'Submitted': return '#ffc107';
                  default: return '#6c757d';
                }
              };

              return (
                <tr key={issue.id} className="link-row border-bottom">
                  <td className="p-2 text-center">{issue.user?.fullName || 'N/A'}</td>
                  <td className="p-2 text-center">{issue.title}</td>
                  <td className="p-2 text-center">
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: getStatusBadgeColor(issue.status),
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {issue.status}
                    </span>
                  </td>
                  <td className="p-2 text-center">{new Date(issue.createdAt).toLocaleDateString()}</td>
                  <td className="p-2 text-center">
                    <Button size="sm" color="info" outline onClick={() => handleView(issue)}>View</Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {pagination && (
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      )}

      <Offcanvas isOpen={isOpen} toggle={toggleCanvas} direction="end" className="custom-canvas-width">
        <OffcanvasHeader toggle={toggleCanvas}>
          <strong>Issue Details</strong>
        </OffcanvasHeader>
        <OffcanvasBody>
          {selectedIssue && (
            <div>
              <div className="mb-3">
                <h5>User Information</h5>
                <p><strong>Name:</strong> {selectedIssue.user?.fullName || 'N/A'}</p>
                <p><strong>Email:</strong> {selectedIssue.user?.email || 'N/A'}</p>
                {selectedIssue.user?.phoneNumber && (
                  <p><strong>Phone:</strong> {selectedIssue.user.phoneNumber}</p>
                )}
                {selectedIssue.user?.university && (
                  <p><strong>University:</strong> {selectedIssue.user.university}</p>
                )}
              </div>

              <hr />

              <div className="mb-3">
                <h5>Issue Information</h5>
                <p><strong>Title:</strong> {selectedIssue.title || 'N/A'}</p>
                <p><strong>Status:</strong> 
                  <span style={{ 
                    marginLeft: '8px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: selectedIssue.status === 'Resolved' ? '#28a745' : 
                                    selectedIssue.status === 'Valid Bug' ? '#007bff' :
                                    selectedIssue.status === 'Invalid Bug' ? '#dc3545' :
                                    selectedIssue.status === 'Submitted' ? '#ffc107' : '#6c757d',
                    color: 'white',
                    fontSize: '12px'
                  }}>
                    {selectedIssue.status}
                  </span>
                </p>
                <p><strong>Created:</strong> {new Date(selectedIssue.createdAt).toLocaleString()}</p>
              </div>

              {/* Status Update Buttons */}
              <div className="mb-3">
                <h5>Mark Issue</h5>
                <div className="d-flex gap-2">
                  <Button 
                    color="primary" 
                    onClick={() => handleStatusUpdate('Valid Bug')}
                    disabled={updatingStatus || selectedIssue.status === 'Valid Bug'}
                  >
                    Valid Bug
                  </Button>
                  <Button 
                    color="danger" 
                    onClick={() => handleStatusUpdate('Invalid Bug')}
                    disabled={updatingStatus || selectedIssue.status === 'Invalid Bug'}
                  >
                    Invalid Bug
                  </Button>
                </div>
              </div>

              <div className="mb-3">
                <h5>Description</h5>
                <p style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '15px', 
                  borderRadius: '4px',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word'
                }}>
                  {selectedIssue.description || 'No description provided'}
                </p>
              </div>

              {/* Screenshots */}
              {selectedIssue.screenshots && selectedIssue.screenshots.length > 0 && (
                <div className="mb-3">
                  <h5>Supporting Images ({selectedIssue.screenshots.length})</h5>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {selectedIssue.screenshots.map((screenshot, idx) => (
                      <a
                        key={idx}
                        href={screenshot.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-block', textDecoration: 'none' }}
                      >
                        <img
                          src={screenshot.fileUrl}
                          alt={`Screenshot ${idx + 1}`}
                          style={{
                            width: '150px',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
};

export default IssuesDisplay;
